import { useMemo } from "react";

import {
  type ApolloQueryResult,
  type FetchMoreQueryOptions,
  type NetworkStatus,
  type OperationVariables,
  useQuery,
} from "@apollo/client";

import type {
  GetEventsPendingHumanValidationByTenantQuery,
  GetEventsPendingHumanValidationByTenantQueryVariables,
  HumanValidationEvent,
} from "../../../API";
import { GET_EVENTS_PENDING_HV_BY_TENANT } from "../../../common/operations/queries";
import { errorNotification } from "../../../common/variables/notification";
import client from "../../../configs/apolloClient";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";

interface GetHumanValidatorTableByTenantInterface {
  pendingEvents: HumanValidationEvent[];
  nextToken?: string | null;
  loading: boolean;
  networkStatus: NetworkStatus;
  fetchMore: (
    fetchMoreOptions: FetchMoreQueryOptions<
      GetEventsPendingHumanValidationByTenantQueryVariables,
      GetEventsPendingHumanValidationByTenantQuery
    > & {
      updateQuery?: (
        previousQueryResult: GetEventsPendingHumanValidationByTenantQuery,
        options: {
          fetchMoreResult: GetEventsPendingHumanValidationByTenantQuery;
          variables: OperationVariables;
        }
      ) => GetEventsPendingHumanValidationByTenantQuery;
    }
  ) => Promise<ApolloQueryResult<GetEventsPendingHumanValidationByTenantQuery>>;
}

export const useGetHumanValidatorByTenantTable =
  (): GetHumanValidatorTableByTenantInterface => {
    const customerId = useCustomerIdGuard();

    const handleRequestError = (): void => {
      client.cache
        .reset()
        .then((): void => {
          errorNotification();
        })
        .catch((error): void => {
          console.error(error);
        });
    };

    const { data, loading, fetchMore, networkStatus } = useQuery<
      GetEventsPendingHumanValidationByTenantQuery,
      GetEventsPendingHumanValidationByTenantQueryVariables
    >(GET_EVENTS_PENDING_HV_BY_TENANT, {
      variables: {
        tenantId: customerId,
      },
      notifyOnNetworkStatusChange: true,
      onError: handleRequestError,
    });

    const pendingEvents: HumanValidationEvent[] = useMemo(
      (): HumanValidationEvent[] =>
        (data?.getEventsPendingHumanValidationByTenant
          .items as HumanValidationEvent[]) || [],
      [data?.getEventsPendingHumanValidationByTenant.items]
    );

    const nextToken: string | null | undefined = useMemo(
      (): string | null | undefined =>
        data?.getEventsPendingHumanValidationByTenant.nextToken,
      [data?.getEventsPendingHumanValidationByTenant.items, customerId]
    );

    return { pendingEvents, nextToken, loading, networkStatus, fetchMore };
  };
